import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { useAppLocale } from '../../providers/LocaleProvider';
import type { AppLocale } from '../../i18n';
import { SwitchSpinner } from './SwitchSpinner';

export type LanguageSwitcherVariant = 'compact' | 'pill' | 'settings' | 'nav';

export interface LanguageSwitcherProps {
  variant?: LanguageSwitcherVariant;
  className?: string;
  /** When true, changing language also saves to user profile (default true). */
  persistUser?: boolean;
  dataTourId?: string;
}

const LOCALE_META: Record<
  AppLocale,
  { short: string; native: string; translationKey: 'languageEnglish' | 'languagePersian' }
> = {
  en: { short: 'EN', native: 'English', translationKey: 'languageEnglish' },
  fa: { short: 'FA', native: 'فارسی', translationKey: 'languagePersian' },
};

function LocaleSegment({
  active,
  pending,
  code,
  size,
  settingsTone,
}: {
  code: AppLocale;
  active: boolean;
  pending: boolean;
  size: 'header' | 'settings';
  settingsTone?: boolean;
}) {
  const meta = LOCALE_META[code];
  const sizeClass =
    size === 'settings'
      ? 'min-h-[34px] min-w-[4.5rem] px-3 py-1.5 text-xs gap-1'
      : 'min-h-[17px] min-w-[1.5rem] px-1.5 py-0.5 text-[9px] leading-none gap-0.5 sm:min-h-[24px] sm:min-w-[1.85rem] sm:px-2 sm:py-1 sm:text-[10px]';
  const spinnerClass =
    size === 'settings' ? 'h-3.5 w-3.5 shrink-0' : 'h-2.5 w-2.5 shrink-0 sm:h-3 sm:w-3';

  return (
    <span
      aria-hidden
      className={`relative inline-flex items-center justify-center rounded-full font-semibold transition-colors pointer-events-none ${sizeClass} ${
        active
          ? settingsTone
            ? 'bg-rose-600 text-white shadow-sm dark:bg-rose-500'
            : 'bg-white text-gray-900 shadow-sm ring-1 ring-white/40'
          : settingsTone
            ? 'text-gray-700 dark:text-gray-200'
            : 'text-white/85'
      }`}
    >
      {pending ? (
        <SwitchSpinner className={spinnerClass} />
      ) : (
        <>
          <span className="font-bold tracking-wide">{meta.short}</span>
          {size === 'settings' ? <span className="hidden sm:inline opacity-90">{meta.native}</span> : null}
          {active && size === 'settings' ? (
            <Check className="h-3.5 w-3.5 opacity-90" aria-hidden />
          ) : null}
          {active && size === 'header' ? (
            <Check className="hidden h-2.5 w-2.5 opacity-90 sm:inline" aria-hidden />
          ) : null}
        </>
      )}
    </span>
  );
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant = 'pill',
  className = '',
  persistUser = true,
  dataTourId,
}) => {
  const { t } = useTranslation('common');
  const { locale, setLocale, loading } = useAppLocale();
  const [pendingLocale, setPendingLocale] = useState<AppLocale | null>(null);

  const toggle = () => {
    if (loading || pendingLocale) return;
    const next: AppLocale = locale === 'en' ? 'fa' : 'en';
    setPendingLocale(next);
    void setLocale(next, { persistUser, announce: true }).finally(() => {
      setPendingLocale((current) => (current === next ? null : current));
    });
  };

  const busy = loading || pendingLocale != null;
  // A native disabled button freezes CSS animations on its contents, so the
  // loader stays a still frame. Block input without disabling the control.

  const isCompactChrome = variant === 'compact' || variant === 'nav';
  const segmentSize = variant === 'settings' ? 'settings' : 'header';

  const shellClass =
    variant === 'settings'
      ? 'inline-flex items-center gap-0.5 rounded-full border p-0.5 shadow-sm border-gray-200 bg-gray-50 dark:border-gray-600 dark:bg-gray-800/80 w-full max-w-md'
      : isCompactChrome
        ? 'inline-flex items-center gap-0 rounded-full border p-px shadow-sm backdrop-blur-sm border-white/35 bg-black/15 dark:border-white/25 dark:bg-black/25'
        : `inline-flex items-center gap-0.5 rounded-full border p-0.5 shadow-sm backdrop-blur-sm border-white/30 bg-white/10 dark:border-white/20 dark:bg-black/20`;

  const currentMeta = LOCALE_META[locale];
  const otherMeta = LOCALE_META[locale === 'en' ? 'fa' : 'en'];
  const ariaLabel = `${t('language')}: ${t(currentMeta.translationKey)}. ${t(otherMeta.translationKey)}`;

  return (
    <button
      type="button"
      disabled={loading && pendingLocale == null}
      data-tour-id={dataTourId}
      onClick={toggle}
      aria-label={ariaLabel}
      aria-busy={pendingLocale != null}
      aria-disabled={busy}
      title={`${t(currentMeta.translationKey)} → ${t(otherMeta.translationKey)}`}
      className={`${shellClass} ${className} transition-opacity focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400 disabled:cursor-not-allowed disabled:opacity-60 ${busy ? 'cursor-default' : 'cursor-pointer hover:opacity-95'}`}
    >
      {(['en', 'fa'] as AppLocale[]).map((code) => (
        <LocaleSegment
          key={code}
          code={code}
          active={locale === code}
          pending={pendingLocale === code}
          size={segmentSize}
          settingsTone={variant === 'settings'}
        />
      ))}
    </button>
  );
};
